<template>
  <v-card class="pa-5" elevation="1">
    <v-card-title class="px-0 pt-0">Empresas</v-card-title>

    <DataTable
      no-sync
      :headers="headers"
      :items="filteredCompanies"
      :loading="loading"
      :slots="slots"
    >
      <template v-slot:below-search>
        <v-btn-toggle
          v-model="statusSubscription"
          tile
          color="primary"
          group
          class="mb-2"
        >
          <v-btn value="active">Ativa</v-btn>
          <v-btn value="suspended">Suspensa</v-btn>
          <v-btn value="disabled">Desativada</v-btn>
        </v-btn-toggle>
      </template>

      <template v-slot:actions="{ props: { item } }">
        <ButtonIcon
          color="secondary"
          x-small
          :icon="$icons.view"
          tooltip="Dados da Empresa"
          class="mr-1"
          @click="openCompanyDetailsModal(item)"
        />

        <v-menu
          :nudge-width="220"
          :nudge-left="35"
          :nudge-top="10"
          bottom
          left
          offset-y
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on">
              <v-icon>{{ $icons.dotsVertical }}</v-icon>
            </v-btn>
          </template>

          <v-card>
            <v-list dense>
              <v-list-item v-if="isSuper" link @click="openLoginAsDialog(item)">
                <v-list-item-icon class="mr-2">
                  <v-icon color="secondary" small>{{ $icons.accountSwitch }}</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Logar como</v-list-item-title>
              </v-list-item>

              <v-list-item link @click="openSubscriptionModal(item)">
                <v-list-item-icon class="mr-2">
                  <v-icon color="secondary" small>{{ $icons.list }}</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Assinatura e Pagamentos</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>
      </template>
    </DataTable>

    <Dialog :dialog="dialogDetails" no-title :maxWidth="900">
      <AdminCompanyDetailsModal
        v-if="selectedCompany && dialogDetails"
        slot="content"
        :company="selectedCompany"
        @close="closeCompanyDetailsModal"
      />
    </Dialog>

    <Dialog :dialog="dialogSubscription" no-title :maxWidth="1200">
      <AdminCompanySubscriptionModal
        v-if="selectedCompany && dialogSubscription"
        slot="content"
        :company-id="selectedCompany.id"
        :company-name="selectedCompany.name"
        @close="closeSubscriptionModal"
      />
    </Dialog>

    <DialogConfirmation
      :dialog="dialogLoginAs"
      :loading="loadingLoginAs"
      title="Logar como"
      :message="`Deseja realmente fazer login na empresa ${companySelected?.name}?`"
      @noActionDialogConfirmation="closeLoginAsDialog"
      @yesActionDialogConfirmation="confirmLoginAs"
    />
  </v-card>
</template>

<script>
import { DataTable, ButtonIcon, Dialog } from '@/components/vuetify';
import DialogConfirmation from '@/components/DialogConfirmation';
import AuthService from '@/views/Auth/services/AuthService';
import { getUser, getToken, getTypeUser } from '@/utils';
import { redirectToPanelV4ForUser } from '@/utils/panelV4';
import { TYPE_USER } from '@/utils/enums';
import AdminCompanySubscriptionModal from './components/AdminCompanySubscriptionModal.vue';
import AdminCompanyDetailsModal from './components/AdminCompanyDetailsModal.vue';

export default {
  name: 'AdminCompanies',
  components: {
    DataTable,
    ButtonIcon,
    Dialog,
    DialogConfirmation,
    AdminCompanySubscriptionModal,
    AdminCompanyDetailsModal,
  },
  data() {
    return {
      loading: false,
      loadingLoginAs: false,
      companies: [],
      statusSubscription: 'active',
      dialogDetails: false,
      dialogSubscription: false,
      dialogLoginAs: false,
      selectedCompany: null,
      companySelected: null,
      slots: [{ field: 'actions' }],
      headers: [
        { text: 'Nome', value: 'name' },
        { text: 'Razão Social', value: 'social_name' },
        { text: 'CNPJ', value: 'cnpj' },
        { text: 'Cliente', value: 'customer_name' },
        { text: 'E-mail', value: 'email' },
        { text: 'Ações', value: 'actions', width: 100, align: 'center', sortable: false },
      ],
    };
  },
  computed: {
    isSuper() {
      return getTypeUser() === TYPE_USER.SUPER;
    },
    filteredCompanies() {
      if (this.statusSubscription === 'disabled') {
        return this.companies.filter((company) => company.situation === 'disabled');
      }

      return this.companies.filter((company) => {
        if (company.situation === 'disabled') {
          return false;
        }

        return this.getSubscriptionStatus(company) === this.statusSubscription;
      });
    },
  },
  mounted() {
    this.fetchCompanies();
  },
  methods: {
    getSubscriptionStatus(company) {
      return company.subscriptions?.[0]?.status;
    },
    fetchCompanies() {
      this.loading = true;
      this.$api.admin
        .getCompanies()
        .then(({ data }) => {
          this.companies = (data.companies || []).map((company) => ({
            ...company,
            customer_name: company.customer ? company.customer.name : '-',
          }));
        })
        .catch(() => {
          this.$noty.error('Erro ao carregar as empresas');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openCompanyDetailsModal(company) {
      this.selectedCompany = company;
      this.dialogDetails = true;
    },
    closeCompanyDetailsModal() {
      this.dialogDetails = false;
      this.selectedCompany = null;
    },
    openSubscriptionModal(company) {
      this.selectedCompany = company;
      this.dialogSubscription = true;
    },
    closeSubscriptionModal() {
      this.dialogSubscription = false;
      this.selectedCompany = null;
    },
    openLoginAsDialog(company) {
      this.companySelected = company;
      this.dialogLoginAs = true;
    },
    closeLoginAsDialog() {
      this.dialogLoginAs = false;
      this.companySelected = null;
    },
    confirmLoginAs() {
      const currentUser = getUser();

      if (currentUser.type === 'super') {
        localStorage.setItem(`${import.meta.env.VITE_NAME}.super.token`, getToken());
        localStorage.setItem(
          `${import.meta.env.VITE_NAME}.super.user`,
          JSON.stringify(currentUser)
        );
      }

      this.loadingLoginAs = true;
      this.$api.admin
        .loginAs(this.companySelected.id)
        .then(({ data }) => {
          AuthService.setLocalStorage(data);

          if (redirectToPanelV4ForUser()) return;

          window.location = import.meta.env.VITE_URL;
        })
        .catch(() => {
          this.$noty.error('Erro ao fazer login na empresa');
        })
        .finally(() => {
          this.loadingLoginAs = false;
          this.closeLoginAsDialog();
        });
    },
  },
};
</script>
